import { api } from "@/lib/api";
import type { DocumentoTipo } from "@/types/documento-tipo";
import type { PersonaDocumento } from "@/types/persona-documento";

/**
 * Puerto tipado de la Documentación General del trabajador (tab de
 * `FichaTrabajador`). Una sola implementación: `apiPersonaDocumentosAdapter`.
 *
 * El adaptador mock que existió durante el slice S1b se eliminó cuando el
 * panel que lo usaba dejó de existir: nadie lo importaba y arrastrarlo obligaba
 * a mantener al día un catálogo de 48 tipos hardcodeados con cada cambio de
 * contrato. Si hace falta volver, está en el historial.
 */
export interface PersonaDocumentosUploadOptions {
  /** Reporta el progreso de subida (0-100) cuando el navegador lo soporta. */
  onProgress?: (percent: number) => void;
  /** Permite cancelar la subida en curso (botón "Cancelar" del slot). */
  signal?: AbortSignal;
}

export interface PersonaDocumentosVencimientoOptions {
  /** `null` = "No vence". Fecha ISO `YYYY-MM-DD` cuando sí vence. */
  fechaVencimiento: string | null;
  /** Subconjunto de {7, 15, 30}. Solo tiene efecto si `fechaVencimiento` no es null. */
  notificarDias: number[];
}

/**
 * Catálogo efectivo de la empresa más el estado de "exigibles".
 *
 * `exigiblesConfigurados` NO es `exigibles > 0`: distingue "la empresa todavía
 * no configuró qué pide" de "configuró y no exige ninguno". Los dos casos dan
 * cero exigibles y la pantalla los muestra distinto — sin configurar se habla
 * del catálogo como referencia, en vez de acusar 48 faltantes de golpe.
 */
export interface CatalogoDocumentoTipos {
  tipos: DocumentoTipo[];
  exigibles: number;
  exigiblesConfigurados: boolean;
}

export interface PersonaDocumentosAdapter {
  listTipos(): Promise<CatalogoDocumentoTipos>;
  /** Reemplaza el set completo de tipos que la empresa exige. */
  guardarExigidos(tipoIds: number[]): Promise<CatalogoDocumentoTipos>;
  listDocumentos(personaUuid: string): Promise<PersonaDocumento[]>;
  /**
   * Sube el archivo SOLO — sin vencimiento (siempre queda "No vence" recién
   * subido). El vencimiento se define después vía `updateVencimiento` desde
   * el botón "Vencimiento" del slot (ver rework "subir es inline").
   */
  upload(
    personaUuid: string,
    tipoDocumentoId: number,
    file: File,
    opts?: PersonaDocumentosUploadOptions,
  ): Promise<PersonaDocumento>;
  /** Recibe el `documento` completo (no solo el id) — el llamador ya lo tiene
   * en mano (viene del listado renderizado) y así evitamos un refetch extra
   * solo para resolver `filename_original`. Ver fresh-review de S4b-frontend. */
  download(personaUuid: string, documento: PersonaDocumento): Promise<void>;
  destroy(personaUuid: string, documentoId: number): Promise<void>;
  /** `PATCH /personas/{personaUuid}/documentos/{documentoId}` — fija o limpia
   * el vencimiento de un documento ya subido, independiente de la subida. */
  updateVencimiento(
    personaUuid: string,
    documentoId: number,
    opts: PersonaDocumentosVencimientoOptions,
  ): Promise<PersonaDocumento>;
}

// ---------------------------------------------------------------------------
// Adaptador real (S4b-frontend) — contra el backend implementado en S4b-backend
// (obs #1105/#1104 en engram, `sdd/ficha-trabajador/slice-status`), más el
// endpoint de vencimiento del rework "subir inline" (MR !262):
//   GET    /me/empresa/documento-tipos
//   PUT    /me/empresa/documento-tipos/exigidos
//   GET    /personas/{personaUuid}/documentos
//   POST   /personas/{personaUuid}/documentos            (multipart, defaultea a "No vence")
//   GET    /personas/{personaUuid}/documentos/{id}/download
//   DELETE /personas/{personaUuid}/documentos/{id}
//   PATCH  /personas/{personaUuid}/documentos/{id}       (fecha_vencimiento + notificar_dias)
// ---------------------------------------------------------------------------
interface RespuestaCatalogo {
  data: DocumentoTipo[];
  meta: { exigibles: number; exigibles_configurados: boolean };
}

/** El PUT devuelve el catálogo ya actualizado, con la misma forma que el GET:
 * un guardado no necesita un refetch para que la pantalla quede al día. */
function mapearCatalogo(res: RespuestaCatalogo): CatalogoDocumentoTipos {
  return {
    tipos: res.data,
    exigibles: res.meta.exigibles,
    exigiblesConfigurados: res.meta.exigibles_configurados,
  };
}

export const apiPersonaDocumentosAdapter: PersonaDocumentosAdapter = {
  async listTipos() {
    return mapearCatalogo(
      await api.get<RespuestaCatalogo>("/me/empresa/documento-tipos"),
    );
  },

  async guardarExigidos(tipoIds) {
    return mapearCatalogo(
      await api.put<RespuestaCatalogo>("/me/empresa/documento-tipos/exigidos", {
        tipo_documento_ids: tipoIds,
      }),
    );
  },

  async listDocumentos(personaUuid) {
    const res = await api.get<{ data: PersonaDocumento[] }>(
      `/personas/${personaUuid}/documentos`,
    );
    return res.data;
  },

  async upload(personaUuid, tipoDocumentoId, file, opts) {
    const fd = new FormData();
    fd.append("archivo", file);
    fd.append("tipo_documento_id", String(tipoDocumentoId));
    // Sin `fecha_vencimiento`/`notificar_dias` — el backend defaultea a
    // "No vence" cuando no vienen. El vencimiento se fija después vía
    // `updateVencimiento` (botón "Vencimiento" del slot).
    const res = await api.uploadForm<{ data: PersonaDocumento }>(
      `/personas/${personaUuid}/documentos`,
      fd,
      { onProgress: opts?.onProgress, signal: opts?.signal },
    );
    return res.data;
  },

  async download(personaUuid, documento) {
    // El nombre de descarga lo define el SERVER (Content-Disposition:
    // {label}_{rut}_{fecha}.ext, mismo formato que el ZIP). En una descarga por
    // blob el atributo `a.download` gana sobre el header, así que leemos el
    // nombre del server con `blobWithFilename` (el header se expone por CORS —
    // MR !265) y sólo caemos a `filename_original` si no llegara.
    const { blob, filename } = await api.blobWithFilename(
      `/personas/${personaUuid}/documentos/${documento.id}/download`,
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename ?? documento.filename_original;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke diferido: algunos navegadores tratan la descarga disparada por
    // `a.click()` como asíncrona y revocar el object URL de inmediato puede
    // cancelarla a mitad de camino.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  async destroy(personaUuid, documentoId) {
    await api.deleteJson(`/personas/${personaUuid}/documentos/${documentoId}`);
  },

  async updateVencimiento(personaUuid, documentoId, opts) {
    const res = await api.patch<{ data: PersonaDocumento }>(
      `/personas/${personaUuid}/documentos/${documentoId}`,
      {
        fecha_vencimiento: opts.fechaVencimiento,
        notificar_dias: opts.fechaVencimiento ? opts.notificarDias : [],
      },
    );
    return res.data;
  },
};
